import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tag, Shield, Users, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { workspaceHome, workspacePortalLabel } from '@/lib/workspaces';

const Index = () => {
  const { loading, user, isIdseatAdmin, isClubAdmin, activeWorkspace } = useAuth();
  const navigate = useNavigate();
  // The portal an IDSeat admin who also runs a workspace can jump to.
  const portalHref = activeWorkspace ? workspaceHome(activeWorkspace.kind) : '/club';
  const portalLabel = activeWorkspace ? workspacePortalLabel(activeWorkspace.kind) : 'Tenant Portal';

  useEffect(() => {
    // Wait for auth to load
    if (loading) return;

    // If not logged in, redirect to login
    if (!user) {
      navigate('/login');
      return;
    }

    // A non-IDSeat person goes to the portal of their active workspace
    // (tenant → /club, kiosk → /kiosk, venue → /venue).
    if (!isIdseatAdmin && activeWorkspace) {
      navigate(workspaceHome(activeWorkspace.kind));
      return;
    }

    // If club admin only (not idseat admin), redirect directly to club portal
    if (isClubAdmin && !isIdseatAdmin) {
      navigate('/club');
      return;
    }

    // If idseat admin only (not club admin), redirect to internal admin
    if (isIdseatAdmin && !isClubAdmin) {
      navigate('/admin');
      return;
    }

    // If both roles, show the selector (handled by the render below)
  }, [loading, user, isIdseatAdmin, isClubAdmin, activeWorkspace, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // If not logged in or single-role user, they'll be redirected
  // This UI is only for users with both roles
  if (!user || (!isIdseatAdmin && !isClubAdmin)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Only show selector for users with BOTH roles
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center px-6">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-14 h-14 rounded-xl bg-primary flex items-center justify-center">
            <Tag className="w-7 h-7 text-primary-foreground" />
          </div>
          <span className="font-display text-3xl font-bold">IDSeat</span>
        </div>

        {/* Title */}
        <h1 className="font-display text-2xl font-semibold text-foreground mb-2">
          Admin Portal
        </h1>
        <p className="text-muted-foreground mb-10">
          Select your destination
        </p>

        {/* Admin Buttons - only shown to users with both roles */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="min-w-48">
            <Link to="/admin">
              <Shield className="mr-2 w-5 h-5" /> Internal Admin
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="min-w-48">
            <Link to={portalHref}>
              <Users className="mr-2 w-5 h-5" /> {portalLabel}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Index;
